import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Check, FileUp, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { customerApi, uploadFile, type UploadTicket } from "@repair/api";
import { bytesLabel } from "@repair/contracts";
import { Button, Field, IconButton, PageBanner, Section, TextArea, TextInput } from "@repair/ui";

const formSchema = z.object({
  sn: z.string().trim().min(2, "请填写设备序列号"),
  issue_type: z.string().min(1, "请选择问题类型"),
  fault_desc: z.string().trim().min(8, "请至少用 8 个字描述故障现象"),
  receiver_name: z.string().trim().min(2, "请填写联系人"),
  receiver_phone: z.string().trim().regex(/^1\d{10}$/, "请输入 11 位手机号码"),
  receiver_address: z.string().trim().min(6, "请填写完整寄回地址"),
  return_company: z.string().trim().optional(),
  return_logistics_no: z.string().trim().optional(),
});

type FormData = z.infer<typeof formSchema>;
type UploadEntry = {
  id: string;
  file: File;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  ticket?: UploadTicket;
  error?: string;
};

const draftKey = "repair_react_submit_draft";
const issueOptions = ["无法开机", "连接异常", "功能异常", "外观损坏", "其他问题"];

export function SubmitPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: readDraft(),
    mode: "onTouched",
  });
  const values = form.watch();

  useEffect(() => {
    const timer = window.setTimeout(() => sessionStorage.setItem(draftKey, JSON.stringify(values)), 250);
    return () => window.clearTimeout(timer);
  }, [values]);

  const successfulUploads = useMemo(() => uploads.filter((item) => item.status === "success" && item.ticket), [uploads]);
  const failedUploads = uploads.filter((item) => item.status === "error");
  const busyUploads = uploads.some((item) => item.status === "pending" || item.status === "uploading");

  const submit = useMutation({
    mutationFn: (data: FormData) => customerApi.create({
      ...data,
      fault_desc: `${data.issue_type}：${data.fault_desc}`,
      idempotency_key: crypto.randomUUID(),
      attachments: successfulUploads.map((item) => ({ upload_token: item.ticket!.upload_token })),
    }),
    onSuccess: (result) => {
      sessionStorage.removeItem(draftKey);
      navigate(`/success?id=${encodeURIComponent(result.id)}&order_no=${encodeURIComponent(result.order_no)}`, { replace: true });
    },
  });

  async function addFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const entry: UploadEntry = { id: crypto.randomUUID(), file, progress: 0, status: "pending" };
      setUploads((items) => [...items, entry]);
      await startUpload(entry);
    }
  }

  async function startUpload(entry: UploadEntry) {
    if (entry.file.size > 50 * 1024 * 1024) {
      updateUpload(entry.id, { status: "error", error: "单个文件不能超过 50MB" });
      return;
    }
    try {
      updateUpload(entry.id, { status: "uploading", progress: 0.05, error: undefined });
      const ticket = await customerApi.prepareUpload("new", entry.file);
      if (!ticket) throw new Error("上传凭证生成失败");
      await uploadFile(ticket, entry.file, (progress) => updateUpload(entry.id, { progress }));
      updateUpload(entry.id, { status: "success", progress: 1, ticket });
    } catch (error) {
      updateUpload(entry.id, { status: "error", error: error instanceof Error ? error.message : "上传失败" });
    }
  }

  function updateUpload(id: string, patch: Partial<UploadEntry>) {
    setUploads((items) => items.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  async function next() {
    const fields: Array<keyof FormData> = step === 1
      ? ["sn", "issue_type", "fault_desc"]
      : ["receiver_name", "receiver_phone", "receiver_address"];
    if (await form.trigger(fields)) setStep((value) => Math.min(3, value + 1));
  }

  return (
    <div className="customer-page">
      <header className="customer-page-title">
        <div><h1>提交报修</h1><p>填写设备与寄回信息</p></div>
      </header>
      <div className="submit-steps" aria-label="提交步骤">
        {[1, 2, 3].map((value, index) => (
          <span key={value} className={step === value ? "is-current" : step > value ? "is-complete" : ""}>
            <b>{step > value ? <Check size={12} /> : value}</b>{["设备", "资料", "核对"][index]}{value < 3 && <i />}
          </span>
        ))}
      </div>

      {submit.error && <PageBanner tone="danger" title="提交失败">{submit.error.message}</PageBanner>}
      {failedUploads.length > 0 && <PageBanner tone="attention">{failedUploads.length} 个附件上传失败。可重试或移除，已填写内容不会丢失。</PageBanner>}

      <form onSubmit={form.handleSubmit((data) => submit.mutate(data))} className="customer-surface">
        <div className="customer-surface__body">
          {step === 1 && (
            <Section title="设备与问题">
              <div className="form-grid">
                <Field label="设备 SN" error={form.formState.errors.sn?.message}>
                  <TextInput autoComplete="off" placeholder="机身或包装上的序列号" {...form.register("sn")} />
                </Field>
                <Field label="问题类型" error={form.formState.errors.issue_type?.message}>
                  <div className="choice-grid">
                    {issueOptions.map((option) => (
                      <label className="choice-card" key={option}>
                        <input type="radio" value={option} {...form.register("issue_type")} />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </Field>
                <Field label="故障现象" hint="说明何时发生、能否复现和已尝试的处理" error={form.formState.errors.fault_desc?.message}>
                  <TextArea placeholder="例如：连接电脑约 10 分钟后断开，重启后短暂恢复…" {...form.register("fault_desc")} />
                </Field>
              </div>
            </Section>
          )}

          {step === 2 && (
            <>
              <Section title="附件" meta="图片、视频或 PDF，单个不超过 50MB">
                <label className="upload-zone">
                  <input type="file" multiple accept="image/*,video/*,.pdf" onChange={(event) => void addFiles(event.target.files)} />
                  <FileUp size={22} />
                  <strong>添加故障照片或视频</strong>
                  <span>每个文件单独上传，可失败重试</span>
                </label>
                <div className="upload-list">
                  {uploads.map((entry) => (
                    <div className="upload-item" key={entry.id}>
                      <strong>{entry.file.name}</strong>
                      <div>
                        {entry.status === "error" && <IconButton label="重试" onClick={() => void startUpload(entry)}><RotateCcw size={16} /></IconButton>}
                        <IconButton label="移除" onClick={() => setUploads((items) => items.filter((item) => item.id !== entry.id))}><Trash2 size={16} /></IconButton>
                      </div>
                      <span>{entry.error || `${bytesLabel(entry.file.size)} · ${uploadStatus(entry.status)}`}</span>
                      <div className="upload-progress"><i style={{ width: `${entry.progress * 100}%` }} /></div>
                    </div>
                  ))}
                </div>
              </Section>
              <Section title="完成维修后的寄回地址">
                <div className="form-grid">
                  <div className="form-grid form-grid--two">
                    <Field label="联系人" error={form.formState.errors.receiver_name?.message}><TextInput autoComplete="name" {...form.register("receiver_name")} /></Field>
                    <Field label="手机号" error={form.formState.errors.receiver_phone?.message}><TextInput inputMode="tel" autoComplete="tel" {...form.register("receiver_phone")} /></Field>
                  </div>
                  <Field label="地址" error={form.formState.errors.receiver_address?.message}><TextArea autoComplete="street-address" {...form.register("receiver_address")} /></Field>
                  <div className="form-grid form-grid--two">
                    <Field label="已寄出时填写物流公司（可选）"><TextInput {...form.register("return_company")} /></Field>
                    <Field label="物流单号（可选）"><TextInput {...form.register("return_logistics_no")} /></Field>
                  </div>
                </div>
              </Section>
            </>
          )}

          {step === 3 && (
            <Section title="核对信息">
              <dl className="review-list">
                <div><dt>设备 SN</dt><dd>{values.sn}</dd></div>
                <div><dt>问题</dt><dd>{values.issue_type} · {values.fault_desc}</dd></div>
                <div><dt>附件</dt><dd>{successfulUploads.length ? `${successfulUploads.length} 个已上传` : "未添加"}</dd></div>
                <div><dt>联系人</dt><dd>{values.receiver_name} · {values.receiver_phone}</dd></div>
                <div><dt>寄回地址</dt><dd>{values.receiver_address}</dd></div>
              </dl>
            </Section>
          )}
        </div>
      </form>

      <div className="customer-sticky">
        <div className="customer-sticky__inner">
          {step > 1 && <Button variant="secondary" onClick={() => setStep((value) => value - 1)}>上一步</Button>}
          {step < 3
            ? <Button onClick={() => void next()}>继续</Button>
            : <Button busy={submit.isPending} disabled={busyUploads || failedUploads.length > 0} onClick={form.handleSubmit((data) => submit.mutate(data))}>提交报修</Button>}
        </div>
      </div>
    </div>
  );
}

function readDraft(): Partial<FormData> {
  try {
    return JSON.parse(sessionStorage.getItem(draftKey) || "{}");
  } catch {
    return {};
  }
}

function uploadStatus(status: UploadEntry["status"]) {
  return { pending: "等待上传", uploading: "上传中", success: "已完成", error: "上传失败" }[status];
}
